import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  ArrowLeft, ChevronDown, ChevronRight, RefreshCw, Loader2,
  CheckCircle2, XCircle, Activity, AlertCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OP_LABELS = {
  invoice_sign: { label: 'Certification facture', color: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  refund: { label: 'Avoir / Refund', color: 'bg-slate-50 text-slate-700 border-slate-300' },
  balance: { label: 'Solde stickers', color: 'bg-blue-50 text-blue-700 border-blue-300' },
};

function ApiLogRow({ log }) {
  const [expanded, setExpanded] = useState(false);
  const opMeta = OP_LABELS[log.operation_type] || { label: log.operation_type, color: 'bg-slate-50 text-slate-700' };
  const Icon = log.success ? CheckCircle2 : XCircle;
  const iconColor = log.success ? 'text-emerald-600' : 'text-rose-600';

  return (
    <>
      <TableRow
        className={`cursor-pointer hover:bg-slate-50 ${!log.success ? 'bg-rose-50/40' : ''}`}
        onClick={() => setExpanded(e => !e)}
        data-testid={`fne-apilog-row-${log.id}`}
      >
        <TableCell className="w-8">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </TableCell>
        <TableCell>
          <Icon className={`h-4 w-4 ${iconColor}`} />
        </TableCell>
        <TableCell>
          <Badge variant="outline" className={opMeta.color}>{opMeta.label}</Badge>
        </TableCell>
        <TableCell className="text-xs font-mono text-slate-500 max-w-[200px] truncate" title={log.endpoint}>
          {log.endpoint}
        </TableCell>
        <TableCell className="text-xs">
          <Badge variant={log.http_status >= 400 ? 'destructive' : 'outline'}>
            {log.http_status ?? '—'}
          </Badge>
        </TableCell>
        <TableCell className="text-xs text-slate-500">{log.latency_ms != null ? `${log.latency_ms}ms` : '—'}</TableCell>
        <TableCell className="text-xs">
          <Badge variant="outline">{log.environment?.toUpperCase()}</Badge>
          {log.mock && <Badge variant="secondary" className="ml-1 text-[10px]">MOCK</Badge>}
        </TableCell>
        <TableCell className="text-xs text-slate-500 max-w-[160px] truncate">
          {log.target_type && (
            <span className="font-mono">{log.target_type}#{(log.target_id || '').slice(0, 8)}</span>
          )}
        </TableCell>
        <TableCell className="text-[11px] text-slate-400 whitespace-nowrap">
          {log.createdAt ? new Date(log.createdAt).toLocaleString('fr-FR') : ''}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow data-testid={`fne-apilog-details-${log.id}`}>
          <TableCell colSpan={9} className="bg-slate-50 p-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-xs">
              <div>
                <div className="font-semibold text-slate-700 mb-1">Payload envoyé</div>
                <pre className="p-2 bg-white border rounded text-[11px] overflow-x-auto max-h-80 whitespace-pre-wrap break-words">
                  {JSON.stringify(log.request_payload, null, 2)}
                </pre>
              </div>
              <div>
                <div className="font-semibold text-slate-700 mb-1">
                  Réponse FNE {log.success ? '(succès)' : '(échec)'}
                </div>
                <pre className="p-2 bg-white border rounded text-[11px] overflow-x-auto max-h-80 whitespace-pre-wrap break-words">
                  {JSON.stringify(log.response_payload, null, 2)}
                </pre>
              </div>
              {log.error_message && (
                <div className="lg:col-span-2">
                  <div className="font-semibold text-rose-700 mb-1">Message d&apos;erreur</div>
                  <pre className="p-2 bg-rose-50 border border-rose-200 rounded text-[11px] text-rose-800 whitespace-pre-wrap">
                    {log.error_message}
                  </pre>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function FneApiLogs() {
  const [logs, setLogs] = useState([]);
  const [counts, setCounts] = useState({ total: 0, success_count: 0, error_count: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filter === 'invoice_sign' || filter === 'refund' || filter === 'balance') {
        params.set('operation_type', filter);
      } else if (filter === 'errors') {
        params.set('success', 'false');
      } else if (filter === 'success') {
        params.set('success', 'true');
      }
      const { data } = await axios.get(`${API}/fne/api-logs?${params.toString()}`);
      setLogs(data.items || []);
      setCounts({
        total: data.total,
        success_count: data.success_count,
        error_count: data.error_count,
      });
    } catch (err) {
      console.error('FNE api-logs load error', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const errorRate = counts.total > 0 ? Math.round((counts.error_count / counts.total) * 100) : 0;

  return (
    <div className="space-y-6 p-6" data-testid="fne-api-logs-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <Link to="/admin/fne" className="text-xs text-slate-500 hover:underline flex items-center gap-1 mb-2">
            <ArrowLeft className="h-3 w-3" /> Retour au tableau de bord FNE
          </Link>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6 text-emerald-600" />
            Journal technique FNE (API logs)
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Toutes les requêtes envoyées à l&apos;API DGI (payload + réponse + statut HTTP + latence).
            Utile pour debug et audit fiscal.
          </p>
        </div>
        <Button variant="outline" onClick={load} size="sm" data-testid="fne-apilog-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Rafraîchir
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4">
          <div className="text-xs text-slate-500">Total appels</div>
          <div className="text-2xl font-bold">{counts.total}</div>
        </CardContent></Card>
        <Card className="border-emerald-200 bg-emerald-50"><CardContent className="pt-4">
          <div className="text-xs text-emerald-700">Succès</div>
          <div className="text-2xl font-bold text-emerald-700">{counts.success_count}</div>
        </CardContent></Card>
        <Card className="border-rose-200 bg-rose-50"><CardContent className="pt-4">
          <div className="text-xs text-rose-700">Échecs</div>
          <div className="text-2xl font-bold text-rose-700">{counts.error_count}</div>
        </CardContent></Card>
        <Card className={errorRate > 10 ? 'border-amber-300 bg-amber-50' : ''}><CardContent className="pt-4">
          <div className="text-xs text-slate-500">Taux d&apos;erreur</div>
          <div className="text-2xl font-bold">{errorRate}%</div>
        </CardContent></Card>
      </div>

      {/* Filtres */}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger value="all" data-testid="fne-apilog-tab-all">Tous</TabsTrigger>
          <TabsTrigger value="invoice_sign" data-testid="fne-apilog-tab-sign">Certifications</TabsTrigger>
          <TabsTrigger value="refund" data-testid="fne-apilog-tab-refund">Avoirs</TabsTrigger>
          <TabsTrigger value="success" data-testid="fne-apilog-tab-success">Succès</TabsTrigger>
          <TabsTrigger value="errors" data-testid="fne-apilog-tab-errors">Échecs</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{logs.length} appel(s) affiché(s)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm flex flex-col items-center gap-2">
              <AlertCircle className="h-6 w-6" />
              Aucun appel FNE journalisé. Lancez une certification pour générer un log.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Opération</TableHead>
                  <TableHead>Endpoint</TableHead>
                  <TableHead>HTTP</TableHead>
                  <TableHead>Latence</TableHead>
                  <TableHead>Env</TableHead>
                  <TableHead>Cible</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map(log => <ApiLogRow key={log.id} log={log} />)}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
